import { equals } from "angular";
import { Text } from "konva/lib/shapes/Text";
import { Rect } from "konva/lib/shapes/Rect";
import { inlineEdit } from "../behaviors/inline-edit";
import { IFontState, IHasTextColor, IHasHighlight } from "./util";
import { fireStateEvent } from "../events";
import { WhiteboardShapeStateType } from ".";
import { WhiteboardShape, IShapeState } from "./shape";
import { IWhiteboardRendererContext } from "../whiteboard-renderer";
import { localize } from "@gtmhub/localization";

export interface IHeadingState extends IShapeState, IFontState, IHasTextColor, IHasHighlight {
  type: "heading";
  content: string;
}

export class Heading extends WhiteboardShape<IHeadingState> {
  private contentNode: Text;
  private topPadNode: Rect;
  private bottomPadNode: Rect;
  private middlePadNode: Rect;
  private placeholder: string;

  static stateTypes: WhiteboardShapeStateType[] = ["heading"];

  constructor(state: IHeadingState, context: IWhiteboardRendererContext) {
    super(state, context);

    this.placeholder = `${localize("start_typing")}...`;

    this.pad(state);
    this.content(state);
    this.resize(state);
  }

  updateState(state: IHeadingState): boolean {
    let updated = false;
    if (
      this.state.content !== state.content ||
      this.state.fontSize != state.fontSize ||
      this.state.fontFamily !== state.fontFamily ||
      this.state.bold !== state.bold ||
      this.state.italic !== state.italic ||
      this.state.strikeThrough !== state.strikeThrough ||
      this.state.underline !== state.underline ||
      this.state.textColor !== state.textColor
    ) {
      this.content(state);
      this.resize(state);
      updated = true;
    }
    if (this.state.highlightColor !== state.highlightColor) {
      this.pad(state);
      updated = true;
    }
    if (!equals(this.state.position, state.position)) {
      this.position(state.position);
      updated = true;
    }
    this.state = state;
    return updated;
  }

  private pad(state: IHeadingState) {
    if (!this.topPadNode) {
      this.topPadNode = new Rect({ cornerRadius: 33, opacity: 0.3 });
      this.middlePadNode = new Rect({ cornerRadius: 33, opacity: 0.3 });
      this.bottomPadNode = new Rect({ cornerRadius: 33, opacity: 0.3 });

      this.add(this.middlePadNode, this.topPadNode, this.bottomPadNode);
    }

    this.topPadNode.fill(state.highlightColor);
    this.middlePadNode.fill(state.highlightColor);
    this.bottomPadNode.fill(state.highlightColor);
  }

  private content(state: IHeadingState) {
    if (!this.contentNode) {
      this.contentNode = new Text({});
      this.add(this.contentNode);

      inlineEdit(this, this.contentNode, {
        multiline: false,
        autoGrow: true,
        placeholder: this.placeholder,
        textColor: this.state.textColor,
        onUpdate: (evt, reason, value) => {
          this.contentNode.text(value || this.placeholder);
          this.resize(state);
          this.getLayer().batchDraw();

          if (reason === "outsideClick" || reason === "enter") {
            value = value.trim();
            if (value === this.placeholder) {
              value = "";
            }

            if (value !== this.state.content) {
              fireStateEvent("stateChange", evt, this, { state: this.state, changes: { content: value } });
            }
          }
        },
      });
    }

    this.contentNode.setAttrs({
      text: state.content || this.placeholder,
      fontSize: state.fontSize,
      fontFamily: state.fontFamily,
      fontStyle: [state.bold ? "bold" : "", state.italic ? "italic" : ""].join(" ").trim(),
      textDecoration: [state.strikeThrough ? "line-through" : "", state.underline ? "underline" : ""].join(" ").trim(),
      fill: state.textColor,
    });
  }

  private resize(state: IHeadingState) {
    const contentX = state.fontSize / 2;
    const contentWidth = this.contentNode.width();
    const contentHeight = this.contentNode.height();
    const totalWidth = contentWidth + contentX * 2;

    this.contentNode.position({ x: contentX, y: 0 });

    this.topPadNode.size({ width: totalWidth - contentX, height: contentHeight / 2 }).position({ x: contentX / 2, y: 0 });
    this.middlePadNode.size({ width: contentWidth, height: (contentHeight * 90) / 100 }).position({ x: contentX, y: 0 });
    this.bottomPadNode.size({ width: totalWidth - contentX / 2, height: contentHeight / 2 }).position({ x: 0, y: contentHeight / 2 });
    this.fire("resize");
  }
}
